import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import moment from "moment-timezone";
import { useMemo } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaSort,
  FaSortAlphaDown,
  FaSortAlphaDownAlt,
} from "react-icons/fa";

// ─── Skeleton Rows ────────────────────────────────────────────────────────────
const TableSkeleton = ({ columns = 7, rows = 10 }) => (
  <Tbody>
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <Tr key={rowIdx}>
        {Array.from({ length: columns }).map((_, colIdx) => (
          <Td key={colIdx} py={3}>
            <Skeleton
              height="14px"
              borderRadius="md"
              startColor="gray.100"
              endColor="gray.200"
              width={
                colIdx === columns - 1 ? "24px" : `${60 + Math.random() * 30}%`
              }
            />
          </Td>
        ))}
      </Tr>
    ))}
  </Tbody>
);

// ─── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ sorted }) => {
  if (sorted === "asc")
    return <Icon as={FaSortAlphaDown} color="blue.500" boxSize="12px" />;
  if (sorted === "desc")
    return <Icon as={FaSortAlphaDownAlt} color="blue.500" boxSize="12px" />;
  return <Icon as={FaSort} color="gray.300" boxSize="12px" />;
};

// ─── Main Component ────────────────────────────────────────────────────────────
const EdssTable = ({ searchResult = [], handleOpenCase, isLoading }) => {
  const columns = useMemo(
    () => [
      {
        header: "First Name",
        id: "firstName",
        accessorFn: (row) => row.FIRST_NAME || "—",
      },
      {
        header: "Last Name",
        id: "lastName",
        accessorFn: (row) => row.LAST_NAME || "—",
      },
      {
        id: "birthDate",
        header: "Birth Date",
        accessorFn: (row) =>
          row.BIRTH_DATE
            ? moment(row.BIRTH_DATE, "YYYYMMDD").format("MMM DD, YYYY")
            : "—",
      },
      {
        accessorKey: "GENDER",
        header: "Gender",
        cell: ({ getValue }) => {
          const val = getValue();
          if (!val) return "—";
          return (
            <Badge
              colorScheme={val === "M" ? "blue" : val === "F" ? "pink" : "gray"}
              variant="subtle"
              fontSize="xs"
              px={2}
              borderRadius="full"
            >
              {val === "M" ? "Male" : val === "F" ? "Female" : val}
            </Badge>
          );
        },
      },
      {
        accessorKey: "PRODUCT",
        header: "Product",
        cell: ({ getValue }) => (
          <Text fontWeight="500" fontSize="sm" color="gray.700">
            {getValue() || "—"}
          </Text>
        ),
      },
      {
        id: "eventDate",
        header: "Event Date",
        accessorFn: (row) =>
          row.EVENT_DATE
            ? moment(row.EVENT_DATE, "YYYYMMDD").format("MMM DD, YYYY")
            : "—",
      },
      {
        id: "action",
        header: "Case",
        enableSorting: false,
        cell: ({ row }) => (
          <Tooltip label="Open Case" placement="left" hasArrow>
            <Box
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="30px"
              h="30px"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              cursor="pointer"
              color="gray.500"
              transition="all 0.15s"
              _hover={{
                bg: "blue.50",
                borderColor: "blue.400",
                color: "blue.500",
                transform: "scale(1.05)",
              }}
              onClick={() =>
                handleOpenCase(
                  row.original.id,
                  row.original.PERSON_ID,
                  row.original,
                )
              }
            >
              <Icon as={FaExternalLinkAlt} boxSize="11px" />
            </Box>
          </Tooltip>
        ),
      },
    ],
    [handleOpenCase],
  );

  const table = useReactTable({
    data: searchResult,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 } },
  });

  const { pageIndex } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      overflow="hidden"
      boxShadow="sm"
    >
      {/* Table Scroll Container */}
      <Box overflowX="auto" minH="420px" maxH="420px" overflowY="auto">
        <Table
          variant="simple"
          size="sm"
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, idx) => (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    cursor={header.column.getCanSort() ? "pointer" : "default"}
                    userSelect="none"
                    py={3}
                    px={4}
                    fontSize="xs"
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    color="gray.500"
                    borderBottom="2px solid"
                    borderColor="gray.200"
                    borderRight={
                      idx < header.headerGroup.headers.length - 1
                        ? "1px solid"
                        : "none"
                    }
                    borderRightColor="gray.100"
                    whiteSpace="nowrap"
                    _hover={
                      header.column.getCanSort()
                        ? { color: "blue.500", bg: "blue.50" }
                        : {}
                    }
                    transition="all 0.15s"
                  >
                    <HStack spacing={1}>
                      <Text>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Text>
                      {header.column.getCanSort() && (
                        <SortIcon sorted={header.column.getIsSorted()} />
                      )}
                    </HStack>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          {isLoading ? (
            <TableSkeleton columns={columns.length} rows={12} />
          ) : (
            <Tbody>
              {table.getRowModel().rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} py={16} textAlign="center">
                    <VStack spacing={2} color="gray.400">
                      <Text fontSize="sm" fontWeight="500">
                        No results found
                      </Text>
                      <Text fontSize="xs">
                        Try adjusting your search criteria
                      </Text>
                    </VStack>
                  </Td>
                </Tr>
              ) : (
                table.getRowModel().rows.map((row, rowIdx) => (
                  <Tr
                    key={row.id}
                    bg={rowIdx % 2 === 0 ? "white" : "gray.50"}
                    _hover={{ bg: "blue.50" }}
                    transition="background 0.1s"
                  >
                    {row.getVisibleCells().map((cell, colIdx) => (
                      <Td
                        key={cell.id}
                        py={2.5}
                        px={4}
                        fontSize="sm"
                        color="gray.700"
                        borderBottom="1px solid"
                        borderColor="gray.100"
                        borderRight={
                          colIdx < row.getVisibleCells().length - 1
                            ? "1px solid"
                            : "none"
                        }
                        borderRightColor="gray.100"
                        whiteSpace="nowrap"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          )}
        </Table>
      </Box>

      {/* Footer */}
      <Box
        borderTop="1px solid"
        borderColor="gray.200"
        bg="gray.50"
        px={4}
        py={2.5}
      >
        <HStack justifyContent="space-between" alignItems="center">
          <Box></Box>

          {/* Pagination */}
          <HStack spacing={3}>
            <Button
              size="xs"
              variant="outline"
              leftIcon={<FaChevronLeft size="9px" />}
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
              borderRadius="md"
              borderColor="gray.300"
              color="gray.600"
              _hover={{
                bg: "white",
                borderColor: "blue.400",
                color: "blue.500",
              }}
              _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
            >
              Prev
            </Button>

            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
              Page{" "}
              <Text as="span" fontWeight="700" color="gray.700">
                {pageIndex + 1}
              </Text>{" "}
              of{" "}
              <Text as="span" fontWeight="700" color="gray.700">
                {pageCount || 1}
              </Text>
            </Text>

            <Button
              size="xs"
              variant="solid"
              rightIcon={<FaChevronRight size="9px" />}
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
              borderRadius="md"
              colorScheme="blue"
              _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
            >
              Next
            </Button>
          </HStack>
        </HStack>
      </Box>
      <Box mt={4} textAlign="center">
        <HStack
          spacing={1.5}
          color="gray.500"
          alignItems="center"
          mx="auto"
          pl={2}
        >
          <Icon as={FaInfoCircle} boxSize="11px" />
          <Text fontSize="sm">
            Search results are limited to the first 200 records. Please narrow
            criteria or use the{" "}
            <Text
              as="span"
              fontWeight="bold"
              cursor="pointer"
              color="blue.300"
              textDecor="underline"
              onClick={() => (window.location.href = "/Monitor")}
            >
              Case Dashboard
            </Text>{" "}
            for more efficient browsing
          </Text>
        </HStack>
      </Box>
    </Box>
  );
};

export default EdssTable;
